/**
 * A/B test results — `split-tests/:testSlugId`.
 *
 * A header (DetailHeader: back, name, status chip, the facts line, Stop /
 * Resume, Share, ⋯), a health bar only when something is off, then one well:
 * Goals (click a row to make it the goal), the selected goal in detail
 * (answer, what the call needs, versions, change over time), Engagement and
 * a collapsed Setup. The view model lives in `useExperimentDetailModel`; goal
 * and health logic in `src/lib/experiments/`. Catches are toast-only (see
 * CLAUDE.md Observability): the mutation hooks own reporting.
 */
import { useState } from "react"
import { useNavigate, useOutletContext, useParams } from "react-router-dom"
import { HugeiconsIcon } from "@hugeicons/react"
import { Alert01Icon, ArrowLeft01Icon } from "@hugeicons/core-free-icons"
import { FeatureGate } from "@/components/shared/FeatureGate"
import { PageShell } from "@/components/shared/layout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import ShareExperimentModal from "@/components/experiments/ShareExperimentModal"
import { DetailHeader } from "@/components/experiments/results/DetailHeader"
import { EngagementCard, GoalsCard } from "@/components/experiments/results/GoalsCard"
import { GoalDetailCard } from "@/components/experiments/results/GoalDetailCard"
import { Hint, type ResultRow } from "@/components/experiments/results/ResultRows"
import { SetupDisclosure } from "@/components/experiments/results/SetupDisclosure"
import { clickTargetName, useExperimentDetailModel, type ExperimentDetailModel } from "@/hooks/useExperimentDetailModel"
import type { ExperimentVariant } from "@/hooks/useExperiments"
import { useSiteSafe } from "@/lib/contexts/SiteContext"
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext"
import { formatNumeric } from "@/lib/utils/stringUtils"
import { cn } from "@/lib/utils"

interface DashboardContext {
  propertyId?: string
  currentDomain?: string
  token?: string
  isShareMode?: boolean
}

const PAGE_PADDING = "px-4 pt-6 pb-9 sm:px-9 sm:pt-7"

function SplitTestDetailInner() {
  const { testSlugId, workspaceId, propertySlugId } = useParams<{ testSlugId: string; workspaceId?: string; propertySlugId?: string }>()
  const navigate = useNavigate()
  const { propertyId, currentDomain, token: shareToken, isShareMode } = useOutletContext<DashboardContext>()
  const siteContext = useSiteSafe()
  const workspaceContext = useWorkspaceSafe()
  const domain =
    workspaceContext?.properties?.find((p) => p.propertyId === propertyId)?.domain ||
    currentDomain ||
    siteContext?.currentSite?.domain ||
    ""
  const m = useExperimentDetailModel({ testSlugId, propertyId, shareToken, isShareMode, domain })
  const [showShareModal, setShowShareModal] = useState(false)
  const handleBack = () => navigate(-1)

  if (m.isLoading) return <DetailSkeleton />

  const experiment = m.experiment
  if (!experiment) {
    return (
      <PageShell padding={PAGE_PADDING} className="flex flex-col gap-5">
        <Button variant="ghost" onClick={handleBack} className="-ml-2 w-fit">
          <HugeiconsIcon icon={ArrowLeft01Icon} size={16} />
          Back to experiments
        </Button>
        <div className="well">
          <Card className="gap-0">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="mb-4 flex size-12 items-center justify-center rounded-xl border border-border/40 bg-muted/40">
                <HugeiconsIcon icon={Alert01Icon} size={20} className="text-muted-foreground" />
              </div>
              <p className="text-sm font-medium">Experiment not found</p>
              <p className="mt-1 text-sm text-muted-foreground">
                This experiment doesn't exist or you don't have access to it.
              </p>
            </CardContent>
          </Card>
        </div>
      </PageShell>
    )
  }

  const { goal, decision, clickEditor } = m
  const basePath = workspaceId ? `/workspace/${workspaceId}` : propertySlugId ? `/p/${propertySlugId}` : ""
  const leaderLabel = m.variants.length === 2 ? m.variants.find((v) => !v.isControl)?.label : null
  const ratesHeader = `Control → ${leaderLabel ?? "leader"}`
  const goalRows: ResultRow[] = goal.rows.map((r) => ({
    key: r.key,
    label: r.label,
    isPrimary: r.isPrimary,
    available: r.available,
    reason: r.reason,
    rates: r.rates,
    change: r.change,
    chance: r.chance,
    confidence: r.confidence,
    range: r.range,
  }))

  return (
    <PageShell padding={PAGE_PADDING} className="flex flex-col gap-5">
      <DetailHeader
        name={experiment.experimentName}
        statusChip={m.statusChip}
        meta={<HeaderFacts header={m.header} />}
        isActive={m.isActive}
        isPaused={m.isPaused}
        readOnly={m.readOnly}
        onBack={handleBack}
        onStop={m.actions.stop}
        onResume={m.actions.resume}
        onShare={() => setShowShareModal(true)}
        onExport={m.actions.exportCsv}
        onArchive={m.actions.archive}
        stopping={m.pending.stop}
        resuming={m.pending.resume}
      />

      {m.isActive && m.health.status !== "healthy" && m.health.reasons[0] && (
        <div
          role="status"
          className={cn(
            "flex items-center gap-2.5 rounded-xl px-4 py-2.5 text-[13px] leading-[18px]",
            m.health.status === "check" ? "bg-warning-tint text-foreground ring-1 ring-warning/25" : "bg-muted text-muted-foreground",
          )}
        >
          {m.health.status === "check" && <HugeiconsIcon icon={Alert01Icon} size={15} className="shrink-0 text-warning" />}
          {m.health.reasons[0]}
        </div>
      )}

      <div className="well flex flex-col gap-2">
        <GoalsCard
          rows={goalRows}
          keyClicksKey={goal.keyClicksKey}
          selectedKey={goal.selectedKey}
          onSelect={m.actions.selectGoal}
          ratesHeader={ratesHeader}
          multi={goal.comparison.multi}
          loading={m.metricsLoading}
          readOnly={m.readOnly}
          editDisabledReason={goal.editDisabledReason}
          connectStripeHref={goal.stripeConnected || m.readOnly ? null : `${basePath}/connectors`}
          clickEditor={{
            open: clickEditor.open,
            onOpenChange: m.actions.closeClickEditor,
            onEditKeyClicks: m.actions.editKeyClicks,
            onAddClicks: m.actions.addClicks,
            draft: clickEditor.draft,
            onDraftChange: m.actions.setClickDraft,
            elements: goal.elements,
            versions: m.variants.map((v) => ({ letter: v.letter, label: v.label })),
            nameOf: clickTargetName,
            preview: clickEditor.preview,
            onUse: m.actions.useClickDraft,
            saving: goal.saving,
          }}
          onAddDestination={m.actions.addDestination}
        />

        <GoalDetailCard
          result={goal.result}
          comparison={goal.comparison}
          isPrimary={goal.isPrimary}
          loading={m.metricsLoading}
          settling={goal.settling}
          callable={decision.callable}
          status={m.call.status}
          earliestCall={m.call.earliestCall}
          gates={m.call.gates}
          readOnly={m.readOnly}
          canCall={decision.canCall}
          canShip={decision.canShip}
          onCall={m.actions.call}
          onShip={m.actions.ship}
          calling={m.pending.call}
          shipping={m.pending.ship}
          chart={{ timeSeries: m.timeSeries, dispersion: m.dispersion }}
        />

        <EngagementCard rows={m.engagement} ratesHeader={ratesHeader} loading={m.metricsLoading} />

        <SetupDisclosure rows={m.setupRows} isActive={m.isActive} />
      </div>

      <ShareExperimentModal
        experiment={experiment}
        variants={m.variants as ExperimentVariant[]}
        winner={decision.winner as ExperimentVariant | null}
        significance={Math.round(decision.chance * 1000) / 10}
        open={showShareModal}
        onOpenChange={setShowShareModal}
      />
    </PageShell>
  )
}

/** "1,423 visitors · Split 51.7 / 48.3, balanced · Enough data ~Dec 13 · acme.com/pricing" */
function HeaderFacts({ header }: { header: ExperimentDetailModel["header"] }) {
  const split = header.split
  const facts = [
    <span key="v">{formatNumeric(header.visitors)} visitors</span>,
    split.state === "ok" || split.state === "warn" ? (
      <Hint key="s" text={split.detail ?? "Share of visitors each version got"}>
        <span className={cn("cursor-help", split.state === "warn" && "font-medium text-warning")}>
          Split {split.value}, {split.state === "ok" ? "balanced" : "uneven"}
        </span>
      </Hint>
    ) : null,
    header.enoughData ? (
      <Hint key="e" text="When the test will have enough visitors to call, at today's traffic">
        <span className="cursor-help">
          {header.enoughData === "enough data" ? (
            "Enough data"
          ) : (
            <>
              Enough data <span className="font-medium text-foreground">{header.enoughData}</span>
            </>
          )}
        </span>
      </Hint>
    ) : null,
    header.pagePath ? <span key="p">{header.pagePath}</span> : null,
  ].filter(Boolean)
  return (
    <>
      {facts.map((f, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <span className="text-border">·</span>}
          {f}
        </span>
      ))}
    </>
  )
}

/** Loading: the loaded layout's geometry, data as blocks (DESIGN.md "Loading States"). */
function DetailSkeleton() {
  return (
    <PageShell padding={PAGE_PADDING} className="flex flex-col gap-5">
      <div className="flex flex-col gap-[10px]">
        <Skeleton className="h-7 w-80" />
        <Skeleton className="h-[22px] w-96" />
      </div>
      <div className="well flex flex-col gap-2">
        <Skeleton className="h-[360px] rounded-xl" />
        <Skeleton className="h-[420px] rounded-xl" />
        <Skeleton className="h-[180px] rounded-xl" />
        <Skeleton className="h-[52px] rounded-xl" />
      </div>
    </PageShell>
  )
}

export default function SplitTestDetail() {
  return (
    <FeatureGate feature="splitTesting">
      <SplitTestDetailInner />
    </FeatureGate>
  )
}
